import React, { useMemo, useState } from 'react';
import { ChevronRight, ChevronDown } from 'lucide-react';

function formatValue(value, metric) {
  if (value == null || value === 0) return '-';
  if (metric === 'deal_count') return Number(value).toLocaleString();
  return '$' + Number(value).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function formatMonth(label) {
  if (!label || !label.match(/^\d{4}-\d{2}$/)) return label;
  const [year, month] = label.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[parseInt(month) - 1]} ${year}`;
}

function formatLabel(label, dimension) {
  if (dimension === 'month') return formatMonth(label);
  return label || 'Unassigned';
}

function buildTree(data, colLabels) {
  const root = { children: new Map(), values: {}, subtotals: {}, total: 0 };

  data.forEach(d => {
    let node = root;
    const dims = d.row_dims || [d.row_label];

    dims.forEach((dimVal, depth) => {
      if (!node.children.has(dimVal)) {
        node.children.set(dimVal, {
          label: dimVal,
          depth,
          children: new Map(),
          values: {},
          subtotals: {},
          total: 0,
        });
      }
      node = node.children.get(dimVal);
    });

    // Leaf node gets the actual values
    node.values[d.col_label] = (node.values[d.col_label] || 0) + d.metric_value;
    node.total = (node.total || 0) + d.metric_value;
  });

  // Recursively compute subtotals
  function computeSubtotals(node) {
    if (node.children.size === 0) {
      node.subtotals = { ...node.values };
      return;
    }
    const subs = {};
    let total = 0;
    node.children.forEach(child => {
      computeSubtotals(child);
      colLabels.forEach(col => {
        subs[col] = (subs[col] || 0) + (child.subtotals[col] || 0);
      });
      total += child.total;
    });
    node.subtotals = subs;
    node.total = total;
  }

  computeSubtotals(root);
  return root;
}

export default function MatrixTable({
  data,
  rowDimensions,
  rowDimensionLabels,
  colLabels,
  colDimensionLabel,
  colTotals,
  grandTotal,
  metric,
  metricLabel,
  onCellClick,
  // Backward compat
  rowLabels: legacyRowLabels,
  rowTotals: legacyRowTotals,
  rowDimensionLabel: legacyRowDimLabel,
  rowDimension: legacyRowDim,
  colDimension,
}) {
  const [collapsed, setCollapsed] = useState(new Set());

  const isMultiDim = rowDimensions && rowDimensions.length > 1;
  const dimLabels = rowDimensionLabels || [legacyRowDimLabel || 'Group'];
  const dims = rowDimensions || [legacyRowDim || 'stage'];

  // Build tree from data
  const tree = useMemo(() => {
    if (!data || data.length === 0) return null;
    const cols = colLabels || [];
    return buildTree(data, cols);
  }, [data, colLabels]);

  if (!data || data.length === 0) {
    return <div className="report-chart-empty">No data for the selected configuration.</div>;
  }

  const cols = colLabels || [];

  const toggleCollapse = (key) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Render tree rows recursively
  const renderRows = (node, pathValues = [], pathKey = '') => {
    const rows = [];
    const sortedChildren = [...node.children.entries()].sort((a, b) => String(a[0]).localeCompare(String(b[0])));

    sortedChildren.forEach(([label, child]) => {
      const currentPath = [...pathValues, label];
      const currentKey = pathKey ? `${pathKey}|${label}` : label;
      const isCollapsed = collapsed.has(currentKey);
      const hasChildren = child.children.size > 0;
      const depth = child.depth || 0;

      // Group row (has children) or leaf row
      if (hasChildren) {
        rows.push(
          <tr key={`group-${currentKey}`} className="matrix-group-row">
            <td className="row-label" style={{ paddingLeft: 14 + depth * 20 }}>
              <span className="matrix-group-toggle" onClick={() => toggleCollapse(currentKey)}>
                {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                {formatLabel(label, dims[depth])}
              </span>
            </td>
            {cols.map(col => (
              <td key={col} className="matrix-cell" style={{ fontWeight: 600 }}>
                {formatValue(child.subtotals[col], metric)}
              </td>
            ))}
            <td className="row-total">{formatValue(child.total, metric)}</td>
          </tr>
        );

        if (!isCollapsed) {
          rows.push(...renderRows(child, currentPath, currentKey));
        }
      } else {
        // Leaf row
        rows.push(
          <tr key={`leaf-${currentKey}`}>
            <td className="row-label" style={{ paddingLeft: 14 + depth * 20 }}>
              {formatLabel(label, dims[depth])}
            </td>
            {cols.map(col => {
              const value = child.values[col] || 0;
              const hasValue = value !== 0;
              return (
                <td
                  key={col}
                  className={`matrix-cell ${hasValue ? 'cell-clickable' : 'cell-zero'}`}
                  onClick={hasValue && onCellClick ? () => onCellClick(currentPath, col) : undefined}
                >
                  {formatValue(value, metric)}
                </td>
              );
            })}
            <td className="row-total">{formatValue(child.total, metric)}</td>
          </tr>
        );
      }
    });

    return rows;
  };

  return (
    <div className="matrix-table-wrapper">
      <table className="matrix-table">
        <thead>
          <tr>
            <th className="corner-cell">
              {dimLabels.join(' / ')} / {colDimensionLabel || 'Column'}
            </th>
            {cols.map(cl => (
              <th key={cl} className="col-header">
                {formatLabel(cl, colDimension)}
              </th>
            ))}
            <th className="col-total-header">Total</th>
          </tr>
        </thead>
        <tbody>
          {tree && renderRows(tree)}
        </tbody>
        <tfoot>
          <tr className="totals-row">
            <td className="row-label totals-label">Total</td>
            {cols.map(cl => (
              <td key={cl} className="col-total">
                {formatValue(colTotals?.[cl], metric)}
              </td>
            ))}
            <td className="grand-total">{formatValue(grandTotal, metric)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
